<template>
  <div class="home-search-view">
    <div id="redesign-header" :class="{ isUcc: isUccState }">
      <div class="body-container">
        <div v-if="isDesktopView" :class="isUccState ? 'utdcc-logo' : 'utd-logo'" />
        <utd-logo v-else :dark-logo="false" class="home-utd-logo" />
        <home-search-panel />
        <UtdAiHeroBtn v-if="isHeaderRedesign 
                        && !isExpertAiSearchBarButtonFlag && hasExpertClinicianFlag"
                      :class="userLoggedIn ? 'ds1-mt-4' : 'ds1-mt-2'"
                      class="home-ai-header-button" />
      </div>
    </div>
    <home-copyright-disclaimer class="homepage-redesign" />
    <home-search-disclaimer />
    <div class="utd-panels">
      <template v-if="!isMobileOnDesktop && !isSmallScreen">
        <div v-if="!isProspectView && isWnPcuPanelEnabled" class="wn-pcu-panels">
          <home-wn-pcu-panel />
        </div>
        <home-search-awareness-panel />
      </template>
      <template v-else>
        <home-search-awareness-panel />
        <div v-if="!isProspectView && isWnPcuPanelEnabled" class="wn-pcu-panels">
          <home-wn-pcu-panel />
        </div>
      </template>
    </div>
    <MobileProspectFooter v-if="!isHeaderRedesign && !isDesktopView" />
    <RegisterMergeSuccessModal />
    <UtdToastStack>
      <RegisterSuccessToast />
      <EmailVerifiedToast />
    </UtdToastStack>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import HomeSearchDisclaimer from '_acaSrc/components/homePageRedesign/HomeSearchDisclaimer.vue';
import HomeSearchPanel from '_acaSrc/components/homePageRedesign/HomeSearchPanel.vue';
import HomeCopyrightDisclaimer
    from '_acaSrc/components/contents/search/HomeCopyrightDisclaimer.vue';
import HomeSearchAwarenessPanel
    from '_acaSrc/components/homePageRedesign/HomeSearchAwarenessPanel.vue';
import HomeWnPcuPanel from '_acaSrc/components/homePageRedesign/HomeWnPcuPanel.vue';
import UtdLogo from '_acaSrc/components/header/utdLogo/UtdLogo.vue';
import MobileProspectFooter from '_acaSrc/components/footer/MobileProspectFooter.vue';
import RegisterMergeSuccessModal from '_acaSrc/components/register/RegisterMergeSuccessModal.vue';
import RegisterSuccessToast from '_acaSrc/components/register/RegisterSuccessToast.vue';
import EmailVerifiedToast from '_acaSrc/components/register/EmailVerifiedToast.vue';
import UtdToastStack from '_acaSrc/components/shared/stdlib/UtdToastStack.vue';
import { isSmallScreen as checkIsSmallScreen } from '_acaSrc/utility/DOM';
import { getWindow } from '_acaSrc/utility/DOM';
import useRegisterMergeStatus from '_acaSrc/composables/registerMergeStatus.composable';
import { SET_MOBILE_WEB_SEARCH_INTERACTION } from '_acaSrc/store/search.store';
import UtdAiHeroBtn from '../header/UtdAiHeroBtn.vue';

const store = useStore();

const isSmallScreen = ref(checkIsSmallScreen());

const isDesktopView = computed(() => store.getters['device/isDesktopView']);
const isMobileOnDesktop = computed(() => store.getters['device/isMobileOnDesktop']);
const isHeaderRedesign = computed(() => store.getters['feature/isHeaderRedesign']);
const isExpertAiSearchBarButtonFlag =
    computed(() => store.getters['feature/isExpertAiSearchBarButtonFlag']);
const hasExpertClinicianFlag =
    computed(() => store.getters['feature/hasExpertClinicianFlag']);
const isWnPcuPanelEnabled = computed(() => store.getters['feature/isWnPcuPanelEnabled']);
const isUccState = computed(() => store.getters['app/isUccState']);
const isProspectView = computed(() => store.getters['app/isProspectView']);
const userLoggedIn = computed(() => store.getters['user/userLoggedIn']);
const supportTag = computed(() => store.getters['footer/supportTag']);

const { handleMergeSuccess } = useRegisterMergeStatus();

const updateScreenSize = () => {
    isSmallScreen.value = checkIsSmallScreen();
};

onMounted(() => {
    store.commit(`search/${SET_MOBILE_WEB_SEARCH_INTERACTION}`, false);
    getWindow().addEventListener('resize', updateScreenSize);
    handleMergeSuccess(supportTag.value.serverTag);
});

onUnmounted(() => {
    getWindow().removeEventListener('resize', updateScreenSize);
});
</script>

<style scoped lang="less">
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

.utdWkHomePage2024 .home-search-view {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.utd-panels {
  display: flex;
  flex: 1;
}

.utdWkHomePage2024 {
  #redesign-header {
    padding-top: 64px;
    background: url(~_acaAssets/utd-redesign/blue-gradient-background.svg) no-repeat center/cover;

    .isSmallScreen&,
    .isMobileOnDesktop&,
    .mobileonDesktop&,
    .isMobile& {
      background: url(~_acaAssets/utd-redesign/blue-gradient-background-292.png) 
        no-repeat center/cover;

      .anonymous-user&, &.isUcc {
        background: url(~_acaAssets/utd-redesign/blue-gradient-background-232.png) 
          no-repeat center/cover;
      }
    }

    .home-utd-logo {
      .ds1-mt-3();

      .isTablet& {
        .ds1-mt-0();
      }
    }

    .logo-properties {
      background-repeat: no-repeat;
      height: 62px;
      background-position-x: 50%;
      background-position-y: 50%;
    }

    .body-container {
      padding-top: clamp(30px, 5vh, 80px);
      padding-bottom: clamp(30px, 5vh, 80px);

      :deep(.ai-header-button.home-ai-header-button) {
        margin-left: auto;
        margin-right: auto;
      }

      &:has(.search-panel-container:not(.display-shortcuts)) 
      :deep(.ai-header-button.home-ai-header-button) {
        .ds1-mt-0(); 
      }

      :deep(.wk-logo) {
        width: 100vw;
      }

      .isDesktop& {
        :deep(.wk-logo) {
          padding: 0;
          margin: 12px 0 0 24px;

          &:focus-visible {
            .ds1-utd-element-outline(solid, 2px, @WKCE-WHITE);
          }
        }
      }

      .isTablet& {
        :deep(.wk-logo) {
          width: 50vw;
          margin-left: auto;
          margin-right: auto;
        }
      }

      .isSmallScreen&,
      .isMobile&,
      .isMobileOnDesktop&,
      .mobileonDesktop& {
        .ds1-ph-4();
        .ds1-pb-5();
        padding-top: 56px;
        width: unset;

        :deep(.wk-logo) {
          width: 50vw;
          margin-left: auto;
          margin-right: auto;
          margin-top: unset;
        }

        .body-container {
          .ds1-pb-5();
        }

        .anonymous-user&, &.isUcc {
          .body-container {
            .ds1-pb-6();
          }
        }
      }

      :deep(#new-search) {
        display: block;
      }

      .utdcc-logo {
        .logo-properties();
        background-image: url(~_acaAssets/utd-redesign/utdcc-logo.svg);
      }

      .utd-logo {
        .logo-properties();
      background-image: url(~_acaAssets/utd-redesign/utd-logo.svg);
      }
    }
  }
}

.isWnPcuPanelEnabled & {
  .utd-panels {
    display: flex;
    flex-direction: column; 
    flex: 1;
  }

  .wn-pcu-panels {
    display: flex;
  }
}

@media only screen and (min-width: 768px) {
  .isWnPcuPanelEnabled .utd-panels {
    flex-direction: row;
  }

  .isWnPcuPanelEnabled .home-search-view {
    min-height: 100vh;
  }

  .utdWkHomePage2024 #redesign-header {
    .ds1-pt-6();
  }
}

</style>